import BreadCrumb from "components/Common/BreadCrumb";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import { createSelector } from "reselect";
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import LabelWrapper from "components/Common/LabelWrapper";
import ModalConfirm from "components/Common/ModalConfirm";
import CountUp from "react-countup";
import CollapseFilter from "components/Common/CollapseFilter";
import ModalPreview from "components/Common/ModalPreview";
import {
  COLOR_STICKER_PACKAGE_TYPE,
  STICKER_PACKAGE_TYPE_OPTION,
} from "helpers/constans";
import {
  formatDateStringToKorea,
  formatNumberWithCommas,
} from "helpers/format";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import TooltipCustom from "components/Common/TooltipCustom";
import { Link } from "react-router-dom";
import CollapseContent from "components/Common/CollapseContent";
import FormPackageDetail from "./FormPackageDetail";
import { getPackageStickers, putPackageSticker } from "store/sticker/thunk";
import FormPackage from "./FormPackage";
import TableGridContainer from "components/Common/TableGridContainer";
import FeatherIcon from "feather-icons-react";
import ModalMultiPreview from "components/Common/ModalMultiPreview";
import stickerApi from "api/stickerApi";
import noImage from "../../assets/images/no-image.jpg";

export interface Tag {
  id: string;
  text: string;
}

registerLocale("en", en);
registerLocale("ko", ko);

const StickerManagement = () => {
  const { t, i18n } = useTranslation();
  const dispatch: any = useDispatch();

  const [query, setQuery]: any = useQueryParams({
    time_request: withDefault(NumberParam, 0),
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 30),
    keyword: withDefault(StringParam, ""),
    type: withDefault(StringParam, ""),
  });

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<any>(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [typeShowMode, setTypeShowMode] = useState<string>("TABLE");

  const [itemToEdit, setItemToEdit] = useState<any>(false);

  const [keywordSearch, setNicknameSearch] = useState<string>(
    query?.keyword || ""
  );

  const [typeSearch, setTypeSearch] = useState<any>(
    STICKER_PACKAGE_TYPE_OPTION?.find((item) => item?.value === query?.type)
      ?.value ?? ""
  );

  const [linkImage, setLinkImage] = useState<any>(null);

  const selectLayoutState = (state: any) => state.PackageSticker;

  const StickerProperties = createSelector(selectLayoutState, (state) => ({
    stickers: state.stickers,
    isStickerLoading: state.isStickerLoading,
    isStickerSuccess: state.isStickerSuccess,

    error: state.error,
  }));

  // Inside your component
  const { stickers, isStickerLoading } = useSelector(StickerProperties);

  function loadTableData() {
    dispatch(getPackageStickers(query));
  }

  useEffect(() => {
    loadTableData();
  }, [dispatch, JSON.stringify(query)]);

  const searchData = () => {
    setQuery({
      ...query,
      keyword: keywordSearch || "",
      type: typeSearch,
      page: 1,
      time_request: +new Date(),
    });
  };

  const resetData = () => {
    setQuery(
      {
        keyword: "",
        type: "",
        time_request: +new Date(),
        page: 1,
      },
      "push"
    );
    setNicknameSearch((_prev) => "");
    setTypeSearch("");
  };

  // Begin::Update User
  const handleClickEdit = (item: any) => {
    setIsOpen(true);
    setItemToEdit(item);
  };

  const onCloseClick = () => {
    setItemToEdit((_prev: any) => null);
    setIsOpen((_prev) => false);
  };

  const triggerRefresh = () => {
    onCloseClick();
    dispatch(getPackageStickers(query));
  };
  //End::Update User

  // Begin::Change status
  const handleOnConfirmUpdate = async (item: {
    id: number | string;
    status: string | boolean;
  }) => {
    try {
      if (!item) {
        return;
      }
      const response: any = await putPackageSticker(item?.id, {
        status: item?.status,
      });
      if (response?.data) {
        dispatch(getPackageStickers(query));
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };
  // End::Change status

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 80,
        thClass: "whitespace-nowrap",
        Cell: ({ ...props }) => {
          return (
            <div>
              {stickers?.meta?.total -
                ((query?.page - 1) * query?.limit + props?.row?.index)}
            </div>
          );
        },
      },
      {
        Header: t("Active"),
        accessor: "status",
        thWidth: 80,
        filterable: false,
        sortable: false,
        thClass: "text-center",
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          return (
            <div
              className="form-check form-switch form-switch-sm text-center"
              dir="ltr"
            >
              <input
                type="checkbox"
                className="form-check-input"
                name="status"
                checked={item?.status === 1}
                onChange={(e) => {
                  const value = e?.target?.checked;
                  handleOnConfirmUpdate({
                    id: item?.id,
                    status: !!value ? "1" : "0",
                  });
                }}
              />
            </div>
          );
        },
      },
      {
        Header: t("Thumbnail"),
        accessor: "thumbnailUrl",
        thClass: "text-center",
        filterable: true,
        sortable: false,
        thWidth: 170,
        Cell: (cell: any) => {
          const imgUrl = cell?.value;

          return (
            <>
              <div className="w-100 text-center " style={{ minWidth: "150px" }}>
                <div
                  style={{ width: "90px" }}
                  className="m-auto cursor-pointer"
                  onClick={() => {
                    setLinkImage(imgUrl);
                    document.body.className = "modal-open_img";
                  }}
                >
                  <img
                    src={imgUrl}
                    className="flex-shrink-0 rounded w-100"
                    alt="thumbnail"
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null; // prevents looping
                      currentTarget.src = noImage;
                    }}
                  />
                </div>
              </div>
            </>
          );
        },
      },
      {
        Header: t("Package name"),
        accessor: "name",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: "180",
        Cell: (cell: any) => {
          return (
            cell?.value && (
              <CollapseContent
                style={{ minWidth: "150px" }}
                dataOriginal={cell?.value}
                isRank={false}
                isInlineBlock={true}
                isShowModal={true}
                titleModal={t("Description")}
                numberCharactor={45}
              />
            )
          );
        },
      },
      {
        Header: t("Description"),
        accessor: "description",
        filterable: true,
        sortable: false,
        thWidth: 250,
        Cell: (cell: any) => {
          return (
            <>
              <CollapseContent
                style={{ minWidth: "200px" }}
                dataOriginal={cell?.value}
                isRank={false}
                isInlineBlock={true}
                isShowModal={true}
                titleModal={t("Description")}
              />
            </>
          );
        },
      },
      {
        Header: t("Total Sticker"),
        accessor: "stickerCount",
        filterable: true,
        sortable: true,
        thWidth: 120,
        thClass: "text-end",
        Cell: (cell: any) => {
          return (
            <>
              <div className="text-end pe-3" style={{ minWidth: "100px" }}>
                {formatNumberWithCommas(cell?.value)}
              </div>
            </>
          );
        },
      },
      {
        Header: t("Type"),
        accessor: "type",
        filterable: false,
        sortable: false,
        thWidth: 140,
        thClass: "text-center",
        Cell: (cell: any) => {
          const status = cell?.value;
          return (
            <div className="text-center" style={{ minWidth: 80 }}>
              <span
                className={`rounded-pill badge bg-${COLOR_STICKER_PACKAGE_TYPE[status] || "secondary"
                  }`}
              >
                {t(
                  STICKER_PACKAGE_TYPE_OPTION?.find(
                    (item: any) => String(item?.value) === String(status)
                  )?.label || ""
                )}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Created at"),
        accessor: "createdAt",
        filterable: true,
        sortable: true,
        thWidth: 150,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end pe-3" style={{ minWidth: "120px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Updated at"),
        accessor: "updatedAt",
        filterable: true,
        sortable: false,
        thWidth: 150,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "120px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Button Action"),
        thClass: "text-center whitespace-nowrap",
        thWidth: 150,
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <ul
              className="list-inline d-flex flex-wrap hstack gap-2 mb-0"
              style={{ justifyContent: "center" }}
            >
              <TooltipCustom
                title={t("Button Update")}
                id={`update-md-${item?.id}`}
              >
                <li className="list-inline-item" id={`update-md-${item?.id}`}>
                  <Link
                    className="btn btn-sm btn-soft-primary edit-item-btn"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsOpen(false);
                      setItemToEdit(item);
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom "></i>
                  </Link>
                </li>
              </TooltipCustom>

              <TooltipCustom
                title={t("Button Delete")}
                id={`delete-md-${item?.id}`}
              >
                <li className="list-inline-item" id={`delete-md-${item?.id}`}>
                  <Link
                    className="btn btn-sm btn-soft-danger edit-item-btn"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setItemToDelete(item);
                    }}
                  >
                    <i className="ri-delete-bin-3-fill align-bottom"></i>
                  </Link>
                </li>
              </TooltipCustom>
            </ul>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language, stickers?.meta?.total, query?.page]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuery({ page: page + 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((_prev: any) => {
      return { ..._prev, ...sortBy };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Begin::Delete
  const handleActionDelete = async (id: any) => {
    if (!id) {
      return;
    }
    try {
      setIsConfirmLoading((_prev) => true);
      const response: any = await stickerApi?.deletePackageSticker(id);

      if (response?.data) {
        loadTableData();
        setIsConfirmLoading((_prev) => false);
        setItemToDelete(false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        setIsConfirmLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  // End::Delete

  const handleChangeViewTable = () => {
    if (typeShowMode === "CARD") {
      setTypeShowMode("TABLE");
    } else setTypeShowMode("CARD");
  };

  const onCloseImageClick = () => {
    setLinkImage((_prev: any) => null);
  };

  useEffect(() => {
    document.title = `${t("Sticker")} | Baive`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("Sticker")} pageTitle={t("Home")} />
          <Row>
            <Col lg={12}>
              <Card
                id="customerList"
                style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
              >
                <CardHeader className="border-0">
                  <div className="d-block d-xl-flex justify-content-between align-items-start">
                    <div
                      className="d-flex w-100 flex-wrap gap-3 gap-md-2 menu-card-statistics justify-content-start mb-3 mb-xl-2 position-relative"
                      style={{ maxWidth: "100%" }}
                    >
                      <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Total Package")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={stickers?.meta?.summary?.totalStickerPackage || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                      <Card className="card-animate mb-0 me-0 me-md-2 mb-1 bg-primary-subtle text-primary border-0">
                        <CardBody className="p-3">
                          <div className="d-flex align-items-center ">
                            <div className="flex-grow-1 overflow-hidden">
                              <p className="text-uppercase fw-medium text-primary text-truncate fs-12 mb-0">
                                {t("Total Sticker")}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-between mt-2 pt-0">
                            <div>
                              <h4 className="fs-20 fw-semibold ff-secondary mb-0">
                                <span className="counter-value text-primary">
                                  <CountUp
                                    start={0}
                                    end={stickers?.meta?.summary?.totalSticker || 0}
                                    duration={1}
                                  />
                                </span>
                              </h4>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </div>

                    <div className="d-flex justify-content-end w-100">
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={() => {
                          setIsOpen(true);
                          setItemToEdit(null);
                        }}
                      >
                        <i className="ri-add-fill align-bottom me-1"></i>{" "}
                        {t("Create Package")}
                      </button>
                    </div>
                  </div>
                </CardHeader>
                <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                  <CollapseFilter>
                    <Row className="g-4 align-items-center mt-0 mt-md-2">
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-md-3 mt-3"
                      >
                        <LabelWrapper
                          isShow={!!keywordSearch}
                          label={t("Keyword")}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Keyword")}...`}
                            value={keywordSearch}
                            onChange={(e) => setNicknameSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-md-3 mt-3"
                      >
                        <LabelWrapper label={t("Package Type")} isShow={true}>
                          <DropdownStatusNew
                            name="package_type"
                            dataList={STICKER_PACKAGE_TYPE_OPTION || []}
                            placeholder={`${t("Booking Type")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={typeSearch}
                            onChangeSelect={(e: any) => setTypeSearch(e?.value)}
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            colors={COLOR_STICKER_PACKAGE_TYPE}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={6}
                        lg={6}
                        className="hstack gap-1 justify-content-center justify-content-md-start mt-md-3"
                      >
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary me-1"
                            onClick={searchData}
                          >
                            <i className="ri-search-line align-bottom me-1"></i>{" "}
                            {t("Button Search")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary fs-14"
                            onClick={resetData}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                            {t("Button Reset")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </CollapseFilter>
                </div>

                <Row className="g-4 align-items-center mt-0 mt-md-2">
                  <Col
                    sm={12}
                    md={12}
                    lg={12}
                    className="hstack gap-1 justify-content-center justify-content-md-end mt-sm-1 mt-md-0"
                  >
                    <div className="ms-2 mx-3 mb-2">
                      <button
                        type="button"
                        className={`${typeShowMode !== "CARD" ? "btn-primary" : "btn-white"
                          } btn px-2 py-1`}
                        onClick={handleChangeViewTable}
                      >
                        <FeatherIcon
                          icon="list"
                          className={`${typeShowMode !== "CARD"
                            ? "text-white"
                            : "text-primary"
                            } icon-dual`}
                        />
                      </button>

                      <button
                        type="button"
                        className={`${typeShowMode === "CARD" ? "btn-primary" : "btn-white"
                          } btn px-2 py-1`}
                        onClick={handleChangeViewTable}
                      >
                        <FeatherIcon
                          icon="grid"
                          className={`${typeShowMode === "CARD"
                            ? "text-white"
                            : "text-primary"
                            } icon-dual`}
                        />
                      </button>
                    </div>
                  </Col>
                </Row>
                <CardBody className="pt-3">
                  <div>
                    <TableGridContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle table-bordered-dashed sticky-table-wink-media"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={stickers?.list?.length ? stickers?.list : []}
                      customPageSize={query.limit}
                      customPageIndex={query.page - 1}
                      totalRecords={stickers?.meta?.total}
                      customPageCount={Math.ceil(
                        Number(stickers?.meta?.total) / Number(query.limit)
                      )}
                      handleChangePage={handleChangePage}
                      manualSorting={true}
                      sorting={{
                        sort_by: query.sort_by,
                        order_by: query.order_by,
                      }}
                      handleChangeSorting={handleChangeSorting}
                      isLoading={isStickerLoading}
                      typeShowMode={typeShowMode}
                      setLinkImage={setLinkImage}
                      handleClickEdit={handleClickEdit}
                      isShowHeaderTableFixedTab={typeShowMode}
                      gridForPage={"package"}
                    />
                  </div>
                  <ToastContainer closeButton={false} limit={1} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {itemToDelete && (
          <ModalConfirm
            header={t("Delete Package")}
            isOpen={itemToDelete}
            isLoading={isConfirmLoading}
            onClose={() => setItemToDelete(false)}
            onConfirm={() => handleActionDelete(itemToDelete?.id)}
          />
        )}

        <ModalPreview
          type={"IMAGE"}
          isOpen={!!linkImage}
          url={linkImage}
          onClose={() => onCloseImageClick()}
        />

        <ModalMultiPreview
          isOpen={!!linkImage?.urls}
          urls={linkImage?.urls || []}
          content={linkImage?.content}
          onClose={() => onCloseImageClick()}
        />

        {isOpen && (
          <FormPackage
            setIsOpen={setIsOpen}
            id={itemToEdit?.id}
            setItemToEdit={setItemToEdit}
            triggerRefresh={triggerRefresh}
            setLinkImage={setLinkImage}
          />
        )}

        {itemToEdit && (
          <Modal
            isOpen={!!itemToEdit}
            centered={true}
            size="xl"
            className="modal-fullscreen"
            scrollable={false}
          >
            <ModalHeader toggle={triggerRefresh}>
              {itemToEdit?.name || ""}
            </ModalHeader>
            <ModalBody>
              <FormPackageDetail
                id={itemToEdit?.id}
                setLinkImage={setLinkImage}
              />
            </ModalBody>
          </Modal>
        )}
      </div>
    </React.Fragment>
  );
};

export default StickerManagement;
